import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useWistorAuth } from '../../api/auth'
import {
  useProject, useProjects,
  useRenameApp, useMoveApp, useToggleAppPublic, useDeleteApp,
  useUpdateAppFields, useDuplicateApp,
} from '../shared/use-v2-queries'
import { useApiClient } from '../shared/api-client'
import { useApiBase } from '../shared/api-base-provider'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import orderBy from 'lodash/orderBy'
import {
  DndContext, DragOverlay, closestCenter,
  useSensor, useSensors, PointerSensor,
  type DragStartEvent, type DragEndEvent,
} from '@dnd-kit/core'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { VersionHistoryButton, VersionHistoryDialog } from '../shared/version-history-dialog'
import { invalidateLegacyViewerConfig } from '../shared/legacy-viewer-cache'

// ─── Types ────────────────────────────────────────────
type AppItem = {
  id: string
  title: string
  path: string
  public: boolean
  noConfigCache?: boolean
  deleted?: string | null
  createdAt?: string
  user?: { id: string; name: string } | null
  group?: string | null
  archived?: boolean
  duplicatedFrom?: { id: string; title: string; path: string } | null
}

// ─── Icons ────────────────────────────────────────────
const ic = {
  search: 'M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z',
  plus: 'M12 5v14M5 12h14',
  edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  move: 'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2zM12 11v6M12 17l3-3M12 17l-3-3',
  lock: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 0110 0v4',
  unlock: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 019.9-1',
  configInner: 'M12 15a3 3 0 100-6 3 3 0 000 6z',
  configOuter:
    'M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
  trash: 'M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2',
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z',
  eyeInner: 'M12 15a3 3 0 100-6 3 3 0 000 6z',
  x: 'M18 6L6 18M6 6l12 12',
  check: 'M20 6L9 17l-5-5',
  arrow: 'M9 18l6-6-6-6',
  folder: 'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z',
  chevronDown: 'M6 9l6 6 6-6',
  chevronRight: 'M9 18l6-6-6-6',
  archive: 'M21 8v13H3V8M1 3h22v5H1zM10 12h4',
  unarchive: 'M21 8v13H3V8M1 3h22v5H1zM12 12v5M12 17l-3-3M12 17l3-3',
  copy: 'M20 9h-9a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-9a2 2 0 00-2-2zM5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1',
  clock: 'M12 2a10 10 0 100 20 10 10 0 000-20zM12 6v6l4 2',
}

function Icon({ d, size = 16, color }: { d: string; size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke={color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

function GripIcon() {
  return (
    <svg width={10} height={14} viewBox="0 0 10 20" fill="currentColor" style={{ opacity: 0.25 }}>
      <circle cx="3" cy="4" r="1.3" /><circle cx="3" cy="10" r="1.3" /><circle cx="3" cy="16" r="1.3" />
      <circle cx="7" cy="4" r="1.3" /><circle cx="7" cy="10" r="1.3" /><circle cx="7" cy="16" r="1.3" />
    </svg>
  )
}

// ─── Hooks ────────────────────────────────────────────
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < breakpoint : false)
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < breakpoint)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [breakpoint])
  return mobile
}

function useIsCompact(breakpoint = 1050) {
  const [compact, setCompact] = useState(typeof window !== 'undefined' ? window.innerWidth < breakpoint : false)
  useEffect(() => {
    const h = () => setCompact(window.innerWidth < breakpoint)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [breakpoint])
  return compact
}

// ─── Shared Components ────────────────────────────────
function V2Dialog({
                    open, onClose, title, children, footer,
                  }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode; footer: React.ReactNode
}) {
  if (!open) return null
  return (
    <div className="v2-dialog-overlay">
      <div className="v2-dialog" onClick={e => e.stopPropagation()}>
        <div className="v2-dialog-header">
          <div className="v2-dialog-title">{title}</div>
          <button className="v2-panel-close" onClick={onClose} type="button" style={{ marginLeft: 'auto' }}>
            <Icon d={ic.x} size={16} />
          </button>
        </div>
        <div className="v2-dialog-body">{children}</div>
        <div className="v2-dialog-footer">{footer}</div>
      </div>
    </div>
  )
}

function MoreMenu({
                    id, actions, openMenuId, setOpenMenuId,
                  }: {
  id: string
  actions: { icon: string; label: string; danger?: boolean; onClick?: () => void }[]
  openMenuId: string | null
  setOpenMenuId: (id: string | null) => void
}) {
  const isOpen = openMenuId === id
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null)

  React.useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 4, left: rect.right })
    }
  }, [isOpen])

  return (
    <div className="v2-more-menu">
      <button
        ref={triggerRef}
        className={`v2-more-trigger ${isOpen ? 'open' : ''}`}
        onClick={(e) => { e.stopPropagation(); setOpenMenuId(isOpen ? null : id) }}
        type="button"
      >
        <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>
      {isOpen && pos && (
        <>
          <div className="v2-more-backdrop" onClick={() => setOpenMenuId(null)} />
          <div
            className="v2-more-dropdown"
            style={{ position: 'fixed', top: pos.top, left: pos.left, right: 'auto', transform: 'translateX(-100%)' }}
          >
            {actions.map((a, i) => (
              <button
                key={i}
                className={`v2-more-item ${a.danger ? 'v2-more-item--danger' : ''}`}
                onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); a.onClick?.() }}
              >
                <Icon d={a.icon} size={14} />
                {a.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function VisBadge({ isPublic }: { isPublic: boolean }) {
  return (
    <span className={`v2-badge ${isPublic ? 'v2-badge--public' : 'v2-badge--private'}`}>
      {isPublic ? 'Public' : 'Private'}
    </span>
  )
}

// ─── App Thumbnail Placeholder ────────────────────────
const THUMB_COLORS = [
  '#6950ff', '#e04080', '#16a34a', '#d97706', '#2563eb',
  '#8b5cf6', '#06b6d4', '#ea580c', '#4f46e5', '#0d9488',
]

function hashCode(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

function AppThumbnail({ title, path, size = 40 }: { title: string; path: string; size?: number }) {
  const color = THUMB_COLORS[hashCode(path) % THUMB_COLORS.length]
  const initials = (title || path)
    .split(/[\s\-_]+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('')
  return (
    <div
      className="v2-app-thumb"
      style={{
        width: size, height: size, minWidth: size,
        borderRadius: 'var(--v2-radius-md)',
        background: `${color}18`, border: `1px solid ${color}30`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.35, fontWeight: 700, color, letterSpacing: '-0.02em', flexShrink: 0,
      }}
    >
      {initials || '?'}
    </div>
  )
}

// ─── DnD Components ───────────────────────────────────
function DraggableAppRow({ appId, children }: { appId: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: appId })
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.35 : 1,
  }
  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      <td style={{ width: 20, padding: '0 2px 0 15px', cursor: 'grab' }} {...listeners}>
        <GripIcon />
      </td>
      {children}
    </tr>
  )
}

function DroppableGroup({ groupId, children }: { groupId: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: groupId })
  return (
    <div ref={setNodeRef} style={{
      marginBottom: 16,
      borderRadius: 'var(--v2-radius-lg)',
      border: isOver ? '2px dashed var(--v2-accent)' : '2px dashed transparent',
      background: isOver ? 'var(--v2-accent-soft)' : undefined,
      transition: 'all 0.15s',
    }}>
      {children}
    </div>
  )
}

// ─── Main Export ───────────────────────────────────────
export function ProjectV2() {
  const { projectPath } = useParams<{ projectPath: string }>()
  const auth = useWistorAuth()
  const history = useHistory()

  useEffect(() => {
    if (auth.status !== 'authorized') history.push('/login', `projects/${projectPath}`)
  }, [auth])

  if (auth.status !== 'authorized') return null
  return <ProjectContent projectPath={projectPath} />
}

// ─── Project Content ──────────────────────────────────
function ProjectContent({ projectPath }: { projectPath: string }) {
  const auth = useWistorAuth()
  const history = useHistory()
  const mobile = useIsMobile()
  const compact = useIsCompact()
  const projectQuery = useProject(projectPath)
  const projectsQuery = useProjects()
  const renameApp = useRenameApp()
  const moveApp = useMoveApp()
  const togglePublic = useToggleAppPublic()
  const deleteApp = useDeleteApp()
  const updateAppFields = useUpdateAppFields()
  const duplicateApp = useDuplicateApp()

  const [search, setSearch] = useState('')
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [renameDialog, setRenameDialog] = useState<{ id: string; title: string } | null>(null)
  const [moveDialog, setMoveDialog] = useState<{ id: string; title: string } | null>(null)
  const [moveTargetId, setMoveTargetId] = useState('')
  const [deleteDialog, setDeleteDialog] = useState<{ id: string; title: string } | null>(null)
  const [configPanel, setConfigPanel] = useState<string | null>(null)
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [versionHistoryApp, setVersionHistoryApp] = useState<AppItem | null>(null)
  const [bulkMoveDialog, setBulkMoveDialog] = useState(false)
  const [bulkMoveTargetId, setBulkMoveTargetId] = useState('')

  // Group & archive state
  const [showArchived, setShowArchived] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [newGroupDialog, setNewGroupDialog] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [emptyGroups, setEmptyGroups] = useState<string[]>([])
  const [renameGroupDialog, setRenameGroupDialog] = useState<string | null>(null)
  const [renameGroupValue, setRenameGroupValue] = useState('')
  const [activeId, setActiveId] = useState<string | null>(null)
  const [duplicateDialog, setDuplicateDialog] = useState<{ id: string; title: string } | null>(null)
  const [duplicateTitle, setDuplicateTitle] = useState('')

  const project = projectQuery.data
  const isSuperadmin = auth.status === 'authorized' && auth.user.role === 'superadmin'
  const canEdit = auth.status === 'authorized' && ['superadmin', 'admin', 'editor'].includes(auth.user.role)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  // All non-deleted viewers
  const allViewers = useMemo(() => {
    if (!project?.viewers) return []
    return orderBy(project.viewers as AppItem[], ['title'], ['asc']).filter(v => !v.deleted)
  }, [project])

  // Filtered (search + archive)
  const viewers = useMemo(() => {
    let items = allViewers
    if (!showArchived) items = items.filter(v => !v.archived)
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter(v => v.title?.toLowerCase().includes(q) || v.path?.toLowerCase().includes(q))
    }
    return items
  }, [allViewers, search, showArchived])

  // Group structure
  const groups = useMemo(() => {
    const map = new Map<string, AppItem[]>()
    const ungrouped: AppItem[] = []
    for (const v of viewers) {
      if (v.group) {
        if (!map.has(v.group)) map.set(v.group, [])
        map.get(v.group)!.push(v)
      } else {
        ungrouped.push(v)
      }
    }
    // Include user-created empty groups
    for (const g of emptyGroups) {
      if (!map.has(g)) map.set(g, [])
    }
    const sorted = orderBy(Array.from(map.entries()), [([n]) => n.toLowerCase()], ['asc'])
    return { sorted, ungrouped }
  }, [viewers, emptyGroups])

  const archivedCount = useMemo(() => allViewers.filter(v => v.archived).length, [allViewers])

  const otherProjects = useMemo(() => {
    if (!projectsQuery.data) return []
    return projectsQuery.data.filter((p: any) => p.path !== projectPath)
  }, [projectsQuery.data, projectPath])

  const openApp = useCallback((app: AppItem) => {
    history.push(`/projects/${projectPath}/${app.path}`)
  }, [history, projectPath])

  const configPanelApp = useMemo(() => {
    if (!configPanel) return null
    // Search in allViewers so archived apps can also open config
    return allViewers.find(v => v.id === configPanel) ?? null
  }, [configPanel, allViewers])

  // Selection
  const toggleSelect = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()) }
  const toggleGroupCollapsed = (name: string) => setCollapsedGroups(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n })

  // ─── Handlers ───
  const handleRename = () => {
    if (!renameDialog?.title.trim()) return
    renameApp.mutate({ id: renameDialog.id, title: renameDialog.title.trim() },
      { onSuccess: () => setRenameDialog(null), onError: () => alert('Failed to rename app.') })
  }

  const handleMove = () => {
    if (!moveDialog || !moveTargetId) return
    moveApp.mutate({ id: moveDialog.id, targetProjectId: moveTargetId },
      { onSuccess: () => { setMoveDialog(null); setMoveTargetId('') }, onError: () => alert('Failed to move app.') })
  }

  const handleDelete = () => {
    if (!deleteDialog) return
    deleteApp.mutate(deleteDialog.id, { onSuccess: () => setDeleteDialog(null), onError: () => alert('Failed to delete app.') })
  }

  const handleTogglePublic = (app: AppItem) => togglePublic.mutate({ id: app.id, isPublic: !app.public })
  const handleToggleArchive = (app: AppItem) => updateAppFields.mutate({ id: app.id, archived: !app.archived })

  const openDuplicateDialog = (app: AppItem) => {
    // Auto-generate a version-bumped title
    const match = app.title.match(/^(.*?)[\s\-_]v(\d+)$/i)
    const nextTitle = match
      ? `${match[1]} v${parseInt(match[2], 10) + 1}`
      : `${app.title} - copy`
    setDuplicateDialog({ id: app.id, title: app.title })
    setDuplicateTitle(nextTitle)
  }

  const handleDuplicate = () => {
    if (!duplicateDialog || !duplicateTitle.trim()) return
    duplicateApp.mutate(
      { id: duplicateDialog.id, title: duplicateTitle.trim() },
      {
        onSuccess: () => { setDuplicateDialog(null); setDuplicateTitle('') },
        onError: (err: any) => alert('Failed to duplicate: ' + (err?.response?.data?.message || err?.message || 'Unknown error')),
      }
    )
  }

  const handleBulkMove = () => {
    if (!bulkMoveTargetId || selected.size === 0) return
    Promise.all(Array.from(selected).map(id => moveApp.mutateAsync({ id, targetProjectId: bulkMoveTargetId })))
      .then(() => { setBulkMoveDialog(false); setBulkMoveTargetId(''); exitSelectMode() })
      .catch(() => alert('Failed to move some apps.'))
  }

  const handleRenameGroup = () => {
    if (!renameGroupDialog || !renameGroupValue.trim()) return
    const apps = viewers.filter(v => v.group === renameGroupDialog)
    Promise.all(apps.map(a => updateAppFields.mutateAsync({ id: a.id, group: renameGroupValue.trim() })))
      .then(() => { setRenameGroupDialog(null); setRenameGroupValue('') })
      .catch(() => alert('Failed to rename group.'))
  }

  const handleDeleteGroup = (groupName: string) => {
    // Ungroup all apps in this group (set group to null)
    const appsInGroup = viewers.filter(v => v.group === groupName)
    const promises = appsInGroup.map(a => updateAppFields.mutateAsync({ id: a.id, group: null }))
    // Also remove from local emptyGroups
    setEmptyGroups(prev => prev.filter(g => g !== groupName))
    if (promises.length > 0) {
      Promise.all(promises).catch(() => alert('Failed to ungroup some apps.'))
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null)
    const { active, over } = event
    if (!over || !canEdit) return
    const appId = active.id as string
    const target = over.id as string
    const newGroup = target === '__ungrouped__' ? null : target.startsWith('group:') ? target.slice(6) : null
    if (newGroup === undefined) return
    const app = viewers.find(v => v.id === appId)
    if (!app || (app.group ?? null) === newGroup) return
    updateAppFields.mutate({ id: appId, group: newGroup })
  }

  const newClicked = () => {
    const id = Array.from({ length: 5 }, () => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'.charAt(Math.floor(Math.random() * 62))).join('')
    history.push(`/projects/${projectPath}/${id}/edit`)
  }

  const appActions = useCallback((app: AppItem) => {
    const a: { icon: string; label: string; danger?: boolean; onClick?: () => void }[] = []
    if (canEdit) {
      a.push({ icon: `${ic.configOuter} ${ic.configInner}`, label: 'Configuration', onClick: () => setConfigPanel(app.id) })
      a.push({ icon: ic.edit, label: 'Rename', onClick: () => setRenameDialog({ id: app.id, title: app.title }) })
      a.push({ icon: ic.copy, label: 'Duplicate', onClick: () => openDuplicateDialog(app) })
      a.push({ icon: app.public ? ic.lock : ic.unlock, label: app.public ? 'Make Private' : 'Make Public', onClick: () => handleTogglePublic(app) })
      a.push({ icon: app.archived ? ic.unarchive : ic.archive, label: app.archived ? 'Unarchive' : 'Archive', onClick: () => handleToggleArchive(app) })
    }
    if (isSuperadmin) {
      a.push({ icon: ic.move, label: 'Move to project', onClick: () => setMoveDialog({ id: app.id, title: app.title }) })
      a.push({ icon: ic.trash, label: 'Delete', danger: true, onClick: () => setDeleteDialog({ id: app.id, title: app.title }) })
    }
    return a
  }, [canEdit, isSuperadmin])

  if (projectQuery.isLoading) return <div className="v2-spinner" />
  if (!project) return <div className="v2-empty"><div className="v2-empty-text">Project not found.</div></div>

  const draggedApp = activeId ? viewers.find(v => v.id === activeId) : null

  // ─── Row renderer ───
  const renderRow = (app: AppItem) => {
    const cells = (
      <>
        <td style={{ width: 42, paddingRight: 0 }}><AppThumbnail title={app.title} path={app.path} size={32} /></td>
        <td className="primary">
          <div
            style={{ cursor: 'pointer', opacity: app.archived ? 0.5 : 1 }}
            onClick={() => selectMode ? toggleSelect(app.id) : openApp(app)}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--v2-accent)' }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {app.title}
              {app.archived && <span className="v2-badge v2-badge--archived" style={{ marginLeft: 2, fontSize: 10 }}>Archived</span>}
            </div>
            {app.duplicatedFrom && (
              <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Copied from: {app.duplicatedFrom.title}
              </div>
            )}
          </div>
        </td>
        <td className="mono" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`/${app.path}`}>/{app.path}</td>
        <td><VisBadge isPublic={app.public} /></td>
        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.user?.name ?? '—'}</td>
        {canEdit && (
          <td className="actions" style={{ textAlign: 'right' }}>
            <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <button className="v2-action v2-action--icon-only" onClick={() => setVersionHistoryApp(app)} title="Version history"><Icon d={ic.clock} size={14} /></button>
              <MoreMenu id={`m-${app.id}`} openMenuId={openMenuId} setOpenMenuId={setOpenMenuId} actions={appActions(app)} />
            </div>
          </td>
        )}
      </>
    )
    // In select mode the leading column carries the checkbox instead of the
    // drag handle, so the column count still matches the header either way.
    if (selectMode) return (
      <tr key={app.id} className={selected.has(app.id) ? 'selected' : ''}>
        <td style={{ width: 20, padding: '0 2px 0 15px' }}>
          <input
            type="checkbox"
            className="v2-checkbox"
            checked={selected.has(app.id)}
            onChange={() => toggleSelect(app.id)}
          />
        </td>
        {cells}
      </tr>
    )

    return canEdit
      ? <DraggableAppRow key={app.id} appId={app.id}>{cells}</DraggableAppRow>
      : <tr key={app.id}>{cells}</tr>
  }

  const tableHead = (
    <thead><tr>
      {canEdit && <th style={{ width: 20 }}></th>}
      <th style={{ width: 42 }}></th>
      <th>App</th>
      <th style={{ width: '16%' }}>Path</th>
      <th style={{ width: '9%' }}>Visibility</th>
      <th style={{ width: '11%' }}>Creator</th>
      {canEdit && <th className="actions" style={{ textAlign: 'right', width: '10%' }}>Actions</th>}
    </tr></thead>
  )

  const renderTable = (apps: AppItem[]) => (
    <div className="v2-table-wrap">
      <div className="v2-table-scroll">
        <table className="v2-table" style={{ minWidth: compact ? 550 : 700, tableLayout: 'fixed' }}>
          {tableHead}
          <tbody>{apps.map(renderRow)}</tbody>
        </table>
      </div>
    </div>
  )

  const renderMobileCard = (app: AppItem) => (
    <div key={app.id} className={`v2-mobile-card ${app.archived ? 'v2-mobile-card--archived' : ''} ${selected.has(app.id) ? 'selected' : ''}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        {selectMode && (
          <input
            type="checkbox"
            className="v2-checkbox"
            style={{ marginRight: 10 }}
            checked={selected.has(app.id)}
            onChange={() => toggleSelect(app.id)}
          />
        )}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0, cursor: 'pointer', flex: 1 }}
             onClick={() => selectMode ? toggleSelect(app.id) : openApp(app)}>
          <AppThumbnail title={app.title} path={app.path} size={38} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--v2-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: app.archived ? 0.5 : 1 }} title={app.title}>
              {app.title}
            </div>
            <div className="v2-path">/{app.path}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {app.archived && <span className="v2-badge v2-badge--archived" style={{ fontSize: 10 }}>Archived</span>}
          <VisBadge isPublic={app.public} />
        </div>
      </div>
      <div className="v2-mobile-card-footer">
        <span style={{ fontSize: 12, color: 'var(--v2-text-tertiary)' }}>{app.user?.name ?? '—'}</span>
        {canEdit && (
          <div style={{ display: 'flex', gap: 2 }}>
            <button className="v2-action v2-action--icon-only" onClick={() => setVersionHistoryApp(app)} title="Version history"><Icon d={ic.clock} size={14} /></button>
            <MoreMenu id={`m-${app.id}`} openMenuId={openMenuId} setOpenMenuId={setOpenMenuId} actions={appActions(app)} />
          </div>
        )}
      </div>
    </div>
  )

  const renderGroupSection = (groupName: string, apps: AppItem[]) => {
    const collapsed = collapsedGroups.has(groupName)
    return (
      <DroppableGroup key={groupName} groupId={`group:${groupName}`}>
        <div className="v2-group-header" onClick={() => toggleGroupCollapsed(groupName)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon d={collapsed ? ic.chevronRight : ic.chevronDown} size={14} />
            <Icon d={ic.folder} size={15} color="var(--v2-accent)" />
            <span className="v2-group-name">{groupName}</span>
            <span className="v2-group-count">{apps.length}</span>
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 2 }}>
              <button className="v2-action v2-action--icon-only" onClick={e => { e.stopPropagation(); setRenameGroupDialog(groupName); setRenameGroupValue(groupName) }} title="Rename group">
                <Icon d={ic.edit} size={12} />
              </button>
              <button className="v2-action v2-action--icon-only v2-action--danger" onClick={e => { e.stopPropagation(); handleDeleteGroup(groupName) }} title="Remove group">
                <Icon d={ic.x} size={12} />
              </button>
            </div>
          )}
        </div>
        {!collapsed && (mobile
            ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 4px 8px' }}>{apps.map(renderMobileCard)}</div>
            : renderTable(apps)
        )}
      </DroppableGroup>
    )
  }

  return (
    <div className="v2-page">
      <div className="v2-breadcrumb">
        <button onClick={() => history.push('/')}>Projects</button>
        <Icon d={ic.arrow} size={12} />
        <span className="v2-breadcrumb-current">{project.title}</span>
      </div>

      <div className="v2-page-header">
        <div>
          <h1 className="v2-page-title">{project.title}</h1>
          <div className="v2-path">/{project.path}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isSuperadmin && !selectMode && <button className="v2-btn v2-btn--secondary" onClick={() => setSelectMode(true)}>Select</button>}
          {selectMode && <button className="v2-btn v2-btn--ghost" onClick={exitSelectMode}>Cancel</button>}
          {canEdit && <button className="v2-btn v2-btn--primary" onClick={newClicked}><Icon d={ic.plus} size={14} />New App</button>}
        </div>
      </div>

      {selectMode && selected.size > 0 && (
        <div className="v2-bulk-bar">
          <span className="v2-bulk-count">{selected.size} selected</span>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button className="v2-btn v2-btn--secondary v2-btn--sm" onClick={() => setBulkMoveDialog(true)}><Icon d={ic.move} size={13} />Move</button>
            <button className="v2-btn v2-btn--ghost v2-btn--sm" onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        </div>
      )}

      {/* Toolbar: search + archive + new group */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="v2-search-wrap" style={{ flex: 1, minWidth: 180 }}>
          <span className="v2-search-icon"><Icon d={ic.search} size={15} /></span>
          <input className="v2-input" placeholder="Search apps..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 38 }} />
        </div>
        <span style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>{viewers.length} apps</span>
        <button
          className={`v2-btn v2-btn--sm ${showArchived ? 'v2-btn--secondary' : 'v2-btn--ghost'}`}
          onClick={() => setShowArchived(!showArchived)} style={{ gap: 4 }}
        >
          <Icon d={ic.archive} size={13} />
          {showArchived ? 'Showing archived' : 'Archived'}
          {archivedCount > 0 && <span style={{ opacity: 0.6 }}>({archivedCount})</span>}
        </button>
        {canEdit && (
          <button className="v2-btn v2-btn--ghost v2-btn--sm" onClick={() => setNewGroupDialog(true)} style={{ gap: 4 }}>
            <Icon d={ic.folder} size={13} />New Group
          </button>
        )}
      </div>

      {/* ─── Content with DnD ─── */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={e => setActiveId(e.active.id as string)} onDragEnd={handleDragEnd}>
        {groups.sorted.map(([name, apps]) => renderGroupSection(name, apps))}

        {groups.ungrouped.length > 0 && (
          <DroppableGroup groupId="__ungrouped__">
            {groups.sorted.length > 0 && (
              <div className="v2-group-header" style={{ opacity: 0.6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon d={ic.chevronDown} size={14} />
                  <span className="v2-group-name">Ungrouped</span>
                  <span className="v2-group-count">{groups.ungrouped.length}</span>
                </div>
              </div>
            )}
            {mobile
              ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{groups.ungrouped.map(renderMobileCard)}</div>
              : renderTable(groups.ungrouped)
            }
          </DroppableGroup>
        )}

        <DragOverlay>
          {draggedApp && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', background: 'var(--v2-bg-raised)', border: '1px solid var(--v2-accent)', borderRadius: 'var(--v2-radius-md)', boxShadow: 'var(--v2-shadow-md)', fontSize: 13, fontWeight: 600 }}>
              <AppThumbnail title={draggedApp.title} path={draggedApp.path} size={28} />
              {draggedApp.title}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {viewers.length === 0 && (
        <div className="v2-empty">
          <div className="v2-empty-text">{search ? 'No apps match your search.' : 'No apps in this project yet.'}</div>
        </div>
      )}

      {/* ─── Dialogs ─── */}
      <V2Dialog open={!!renameDialog} onClose={() => setRenameDialog(null)} title="Rename App"
                footer={<><button className="v2-btn v2-btn--ghost" onClick={() => setRenameDialog(null)}>Cancel</button><button className="v2-btn v2-btn--primary" onClick={handleRename} disabled={renameApp.isPending}>{renameApp.isPending ? 'Saving...' : 'Save'}</button></>}>
        {renameDialog && <div className="v2-field"><label className="v2-label">Title</label><input className="v2-input" value={renameDialog.title} onChange={e => setRenameDialog(d => d ? { ...d, title: e.target.value } : d)} autoFocus onKeyDown={e => e.key === 'Enter' && handleRename()} /></div>}
      </V2Dialog>

      <V2Dialog open={!!moveDialog} onClose={() => { setMoveDialog(null); setMoveTargetId('') }} title={moveDialog ? `Move "${moveDialog.title}"` : 'Move App'}
                footer={<><button className="v2-btn v2-btn--ghost" onClick={() => { setMoveDialog(null); setMoveTargetId('') }}>Cancel</button><button className="v2-btn v2-btn--primary" onClick={handleMove} disabled={!moveTargetId || moveApp.isPending}>{moveApp.isPending ? 'Moving...' : 'Move'}</button></>}>
        <div className="v2-field"><label className="v2-label">Target Project</label>
          <select className="v2-select" value={moveTargetId} onChange={e => setMoveTargetId(e.target.value)}>
            <option value="">Select a project...</option>
            {otherProjects.map((p: any) => <option key={p.id || p._id} value={p.id || p._id}>{p.title}</option>)}
          </select>
        </div>
      </V2Dialog>

      <V2Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} title="Delete App"
                footer={<><button className="v2-btn v2-btn--ghost" onClick={() => setDeleteDialog(null)}>Cancel</button><button className="v2-btn v2-btn--danger" onClick={handleDelete} disabled={deleteApp.isPending}>{deleteApp.isPending ? 'Deleting...' : 'Delete'}</button></>}>
        {deleteDialog && <p style={{ fontSize: 14, color: 'var(--v2-text-secondary)', lineHeight: 1.6 }}>Are you sure you want to delete <strong>"{deleteDialog.title}"</strong>? This is a soft delete.</p>}
      </V2Dialog>

      <V2Dialog open={bulkMoveDialog} onClose={() => { setBulkMoveDialog(false); setBulkMoveTargetId('') }} title={`Move ${selected.size} Apps`}
                footer={<><button className="v2-btn v2-btn--ghost" onClick={() => { setBulkMoveDialog(false); setBulkMoveTargetId('') }}>Cancel</button><button className="v2-btn v2-btn--primary" onClick={handleBulkMove} disabled={!bulkMoveTargetId || moveApp.isPending}>{moveApp.isPending ? 'Moving...' : 'Move'}</button></>}>
        <p style={{ fontSize: 14, color: 'var(--v2-text-secondary)', marginBottom: 16 }}>Move <strong>{selected.size} app{selected.size !== 1 ? 's' : ''}</strong> to another project.</p>
        <div className="v2-field"><label className="v2-label">Target Project</label>
          <select className="v2-select" value={bulkMoveTargetId} onChange={e => setBulkMoveTargetId(e.target.value)}>
            <option value="">Select a project...</option>
            {otherProjects.map((p: any) => <option key={p.id || p._id} value={p.id || p._id}>{p.title}</option>)}
          </select>
        </div>
      </V2Dialog>

      <V2Dialog open={newGroupDialog} onClose={() => { setNewGroupDialog(false); setNewGroupName('') }} title="Create Group"
                footer={<><button className="v2-btn v2-btn--ghost" onClick={() => { setNewGroupDialog(false); setNewGroupName('') }}>Cancel</button><button className="v2-btn v2-btn--primary" onClick={() => { if (newGroupName.trim()) { setEmptyGroups(prev => [...prev, newGroupName.trim()]); setNewGroupDialog(false); setNewGroupName('') } }} disabled={!newGroupName.trim()}>Create</button></>}>
        <div className="v2-field"><label className="v2-label">Group Name</label>
          <input className="v2-input" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="e.g. Homepage, Components..." autoFocus />
        </div>
        <p style={{ fontSize: 12, color: 'var(--v2-text-muted)', marginTop: 4 }}>Groups appear when apps are assigned to them. Use the ⋮ menu on any app → drag it onto the group header.</p>
      </V2Dialog>

      <V2Dialog open={!!renameGroupDialog} onClose={() => { setRenameGroupDialog(null); setRenameGroupValue('') }} title="Rename Group"
                footer={<><button className="v2-btn v2-btn--ghost" onClick={() => { setRenameGroupDialog(null); setRenameGroupValue('') }}>Cancel</button><button className="v2-btn v2-btn--primary" onClick={handleRenameGroup} disabled={!renameGroupValue.trim() || updateAppFields.isPending}>{updateAppFields.isPending ? 'Saving...' : 'Save'}</button></>}>
        <div className="v2-field"><label className="v2-label">Group Name</label><input className="v2-input" value={renameGroupValue} onChange={e => setRenameGroupValue(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleRenameGroup()} /></div>
      </V2Dialog>

      {/* ─── Duplicate Dialog ─── */}
      <V2Dialog
        open={!!duplicateDialog}
        onClose={() => { setDuplicateDialog(null); setDuplicateTitle('') }}
        title={duplicateDialog ? `Duplicate "${duplicateDialog.title}"` : 'Duplicate App'}
        footer={<>
          <button className="v2-btn v2-btn--ghost" onClick={() => { setDuplicateDialog(null); setDuplicateTitle('') }}>Cancel</button>
          <button className="v2-btn v2-btn--primary" onClick={handleDuplicate} disabled={!duplicateTitle.trim() || duplicateApp.isPending}>
            {duplicateApp.isPending ? 'Duplicating...' : 'Duplicate'}
          </button>
        </>}
      >
        {duplicateDialog && (
          <>
            <p style={{ fontSize: 13, color: 'var(--v2-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
              This will create a copy of <strong>{duplicateDialog.title}</strong> with all its configuration.
              The new app will reference the original as its source.
            </p>
            <div className="v2-field">
              <label className="v2-label">New Title</label>
              <input className="v2-input" value={duplicateTitle} onChange={e => setDuplicateTitle(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleDuplicate()} />
            </div>
          </>
        )}
      </V2Dialog>

      {configPanelApp && <AppConfigPanel app={configPanelApp} projectPath={projectPath} onClose={() => setConfigPanel(null)} canEdit={canEdit} />}
      {versionHistoryApp && (
        <VersionHistoryDialog
          viewerId={versionHistoryApp.id}
          viewerTitle={versionHistoryApp.title}
          viewerPath={versionHistoryApp.path}   // ← add this
          projectPath={projectPath}
          onClose={() => setVersionHistoryApp(null)}
          canEdit={canEdit}
        />
      )}
    </div>
  )
}
// ─── App Config Side Panel ────────────────────────────
function AppConfigPanel({
                          app, projectPath, onClose, canEdit,
                        }: {
  app: AppItem; projectPath: string; onClose: () => void; canEdit: boolean
}) {
  const api = useApiClient()
  const { baseUrl } = useApiBase()
  const qc = useQueryClient()

  // Fetch the full viewer config
  const configQuery = useQuery({
    queryKey: ['v2', baseUrl, 'app-config', projectPath, app.path],
    queryFn: async () => {
      const { data } = await api.get(`/api/projects/${projectPath}/viewers/${app.path}`)
      return data
    },
    // The in-app editor writes config outside this cache, and queryClient.ts
    // sets refetchOnMount: false app-wide — so re-read every time this opens.
    refetchOnMount: 'always',
  })

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saved, setSaved] = useState(false)
  const [showVersionHistory, setShowVersionHistory] = useState(false)

  // When config loads, set draft
  useEffect(() => {
    if (configQuery.data) {
      setDraft(JSON.stringify(configQuery.data, null, 2))
    }
  }, [configQuery.data])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (configJson: string) => {
      const parsed = JSON.parse(configJson)
      // Strip fields that are populated references or internal — sending
      // these back would overwrite ObjectId refs with nested objects and
      // corrupt the document.
      const { _id, id, __v, user, project, createdAt, ...safeFields } = parsed
      const { data } = await api.put(
        `/api/projects/${projectPath}/viewers/${app.path}`,
        safeFields,
      )
      return data
    },
    onSuccess: () => {
      invalidateLegacyViewerConfig(app.id)
      setEditing(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
      qc.invalidateQueries({ queryKey: ['v2', baseUrl, 'app-config', projectPath, app.path] })
      // Also refresh the project table so updated app info is reflected
      qc.invalidateQueries({ queryKey: ['v2', baseUrl, 'project', projectPath] })
      // invalidate version history so it picks up the new version
      qc.invalidateQueries({ queryKey: ['v2', baseUrl, 'viewer-versions'] })
    },
    onError: (err: any) => alert('Failed to save: ' + (err?.message || 'Unknown error')),
  })

  const handleSave = () => {
    try {
      JSON.parse(draft) // validate
    } catch (e: any) {
      alert('Invalid JSON: ' + e.message)
      return
    }
    saveMutation.mutate(draft)
  }

  const handleCancel = () => {
    setDraft(configQuery.data ? JSON.stringify(configQuery.data, null, 2) : '')
    setEditing(false)
  }

  return (
    <>
      <div className="v2-panel-backdrop" onClick={onClose} />
      <div className="v2-panel">
        {/* Header */}
        <div className="v2-panel-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="v2-panel-title">Configuration</span>
            {saved && (
              <span className="v2-saved-indicator">
                <Icon d={ic.check} size={14} color="var(--v2-success)" /> Saved
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {canEdit && !editing && !configQuery.isLoading && (
              <button className="v2-btn v2-btn--edit v2-btn--sm" onClick={() => setEditing(true)}>
                <Icon d={ic.edit} size={12} /> Edit
              </button>
            )}
            <VersionHistoryButton onClick={() => setShowVersionHistory(true)} />
            <button className="v2-panel-close" onClick={onClose}>
              <Icon d={ic.x} size={18} />
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="v2-panel-summary">
          <div className="v2-panel-summary-grid">
            <div>
              <div className="v2-panel-summary-label">Title</div>
              <div className="v2-panel-summary-value">{app.title}</div>
            </div>
            <div>
              <div className="v2-panel-summary-label">Path</div>
              <div className="v2-panel-summary-value v2-mono" style={{ fontSize: 12 }}>/{app.path}</div>
            </div>
            <div>
              <div className="v2-panel-summary-label">Visibility</div>
              <div className="v2-panel-summary-value"><VisBadge isPublic={app.public} /></div>
            </div>
            <div>
              <div className="v2-panel-summary-label">Creator</div>
              <div className="v2-panel-summary-value">{app.user?.name ?? 'Unknown'}</div>
            </div>
          </div>
        </div>

        {/* Editor body */}
        <div className="v2-panel-body">
          {configQuery.isLoading ? (
            <div className="v2-spinner" />
          ) : configQuery.isError ? (
            <div style={{ color: 'var(--v2-danger)', fontSize: 14, padding: 20 }}>
              Failed to load config.
            </div>
          ) : editing ? (
            <textarea
              className="v2-code-editor"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              spellCheck={false}
            />
          ) : (
            <pre className="v2-code-block">
              {configQuery.data != null
                ? JSON.stringify(configQuery.data, null, 2)
                : '{}'}
            </pre>
          )}
        </div>

        {/* Footer (edit mode only) */}
        {editing && (
          <div className="v2-panel-footer">
            <button className="v2-btn v2-btn--ghost v2-btn--sm" onClick={handleCancel}>Cancel</button>
            <button className="v2-btn v2-btn--primary v2-btn--sm" onClick={handleSave} disabled={saveMutation.isPending}>
              <Icon d={ic.check} size={13} color="#fff" />
              {saveMutation.isPending ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        )}
      </div>

      {showVersionHistory && (
        <VersionHistoryDialog
          viewerId={app.id}
          viewerTitle={app.title}
          viewerPath={app.path}
          projectPath={projectPath}
          onClose={() => setShowVersionHistory(false)}
          canEdit={canEdit}
        />
      )}
    </>
  )
}